import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus, Trash2, Upload, Search, Loader2,
  Image as ImageIcon, Video, Link as LinkIcon
} from "lucide-react";
import { Card, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";
const SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL || "http://localhost:3001";

function VideoThumbnail({ url }) {
  const videoRef = useRef(null);
  const handleLoadedMetadata = () => {
    if (videoRef.current) videoRef.current.currentTime = 1;
  };
  return (
    <>
      <video
        ref={videoRef}
        src={url}
        muted
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        className="w-full h-full object-cover transition-transform group-hover:scale-110"
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center">
          <Video className="w-4 h-4 text-white ml-0.5" />
        </div>
      </div>
    </>
  );
}

function getDisplayName(item) {
  const raw = item.title || item.url || '';
  const base = raw.split('/').pop();
  return base.replace(/\.[^/.]+$/, '') || 'Unnamed';
}

function getImageSrc(item) {
  if (item.url.startsWith('http')) return item.url;
  return `${SERVER_URL}${item.url}`;
}

export default function MediaManager() {
  const [siteMedia, setSiteMedia] = useState([]);
  const [galleryMedia, setGalleryMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [search, setSearch] = useState("");

  // Upload dialog: null | 'site' | 'gallery'
  const [uploadSection, setUploadSection] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");

  // Video URL dialog
  const [videoDialog, setVideoDialog] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");

  const token = localStorage.getItem("ka_admin_token");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [siteRes, galleryRes] = await Promise.all([
        fetch(`${API_URL}/media?section=site`),
        fetch(`${API_URL}/media?section=gallery`),
      ]);
      const [siteData, galleryData] = await Promise.all([siteRes.json(), galleryRes.json()]);
      setSiteMedia(Array.isArray(siteData) ? siteData : []);
      setGalleryMedia(Array.isArray(galleryData) ? galleryData : []);
    } catch {
      toast.error("Failed to fetch media");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openUpload = (section) => {
    setUploadSection(section);
    setFile(null);
    setPreview(null);
    setTitle("");
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please select a file");
    setIsUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    fd.append("title", title);
    fd.append("section", uploadSection);
    try {
      const res = await fetch(`${API_URL}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      toast.success("Image uploaded");
      setUploadSection(null);
      fetchAll();
    } catch {
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddVideoUrl = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) return toast.error("URL is required");
    try {
      const res = await fetch(`${API_URL}/media/video-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: videoTitle || "Untitled Video", url: videoUrl, section: "gallery" }),
      });
      if (!res.ok) throw new Error();
      toast.success("Video added to gallery");
      setVideoDialog(false);
      setVideoUrl("");
      setVideoTitle("");
      fetchAll();
    } catch {
      toast.error("Failed to add video");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this media item?")) return;
    try {
      const res = await fetch(`${API_URL}/media/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success("Deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = (items) =>
    items.filter(item => getDisplayName(item).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      <Tabs defaultValue="site" className="w-full">
        {/* Header row */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-2">
          <TabsList className="bg-[#1a1a1a]/80 border border-[#8B0000]/30">
            <TabsTrigger value="site" className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white text-gray-400">
              <ImageIcon className="w-4 h-4 mr-2" /> Site Media
            </TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white text-gray-400">
              <Video className="w-4 h-4 mr-2" /> Public Gallery
            </TabsTrigger>
          </TabsList>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search media..."
              className="pl-10 bg-white/5 border-[#8B0000]/30 text-white"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Site Media Tab */}
        <TabsContent value="site">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">Images used across content manager, news thumbnails, and store items.</p>
            <Button onClick={() => openUpload('site')} className="bg-[#8B0000] hover:bg-[#B22222] text-white">
              <Plus className="w-4 h-4 mr-2" /> Upload Image
            </Button>
          </div>
          <MediaGrid items={filtered(siteMedia)} loading={loading} onDelete={handleDelete} onUpload={() => openUpload('site')} emptyLabel="No site media yet. Upload images for use in content." />
        </TabsContent>

        {/* Public Gallery Tab */}
        <TabsContent value="gallery">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">Game screenshots and videos shown on the public /mediagallery page.</p>
            <div className="flex gap-2">
              <Button
                onClick={() => { setVideoDialog(true); setVideoUrl(""); setVideoTitle(""); }}
                className="bg-transparent border border-white/10 text-gray-300 hover:text-white hover:bg-white/5 hover:border-white/20"
              >
                <LinkIcon className="w-4 h-4 mr-2" /> Add Video URL
              </Button>
              <Button onClick={() => openUpload('gallery')} className="bg-[#8B0000] hover:bg-[#B22222] text-white">
                <Plus className="w-4 h-4 mr-2" /> Upload Screenshot
              </Button>
            </div>
          </div>
          <MediaGrid items={filtered(galleryMedia)} loading={loading} onDelete={handleDelete} onUpload={() => openUpload('gallery')} emptyLabel="No gallery media yet. Upload screenshots or add video URLs." />
        </TabsContent>
      </Tabs>

      {/* Upload Image Dialog */}
      <Dialog open={!!uploadSection} onOpenChange={(open) => !open && setUploadSection(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#8B0000]/30 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {uploadSection === 'gallery' ? 'Upload Screenshot' : 'Upload Site Image'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 py-4">
            <div
              className="border-2 border-dashed border-[#8B0000]/20 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 transition-all"
              onClick={() => document.getElementById("mediaFileInput").click()}
            >
              {preview ? (
                <img src={preview} alt="Preview" className="w-full max-h-48 object-contain rounded-md mb-2" />
              ) : (
                <Upload className="w-10 h-10 text-gray-500 mb-2" />
              )}
              <p className="text-xs text-gray-400">Click to select an image</p>
              <input id="mediaFileInput" type="file" className="hidden" onChange={handleFileChange} accept="image/*" />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-gray-400">Title (Optional)</label>
              <Input
                placeholder="e.g. Castle Siege Screenshot"
                className="bg-white/5 border-[#8B0000]/30 text-white"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <DialogFooter className="gap-2">
              <DialogClose asChild>
                <Button variant="outline" type="button" className="border-[#8B0000]/30 text-gray-400">Cancel</Button>
              </DialogClose>
              <Button type="submit" className="bg-[#8B0000] hover:bg-[#B22222] text-white" disabled={isUploading || !file}>
                {isUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : "Upload"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Video URL Dialog */}
      <Dialog open={videoDialog} onOpenChange={setVideoDialog}>
        <DialogContent className="bg-[#0a0a0a] border-[#8B0000]/30 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-[#8B0000]" /> Add Video to Gallery
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddVideoUrl} className="space-y-4 py-4">
            <div className="space-y-1">
              <label className="text-sm text-gray-400">Video URL *</label>
              <Input
                placeholder="https://youtube.com/watch?v=..."
                className="bg-white/5 border-[#8B0000]/30 text-white"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-gray-400">Title</label>
              <Input
                placeholder="e.g. Gameplay Trailer"
                className="bg-white/5 border-[#8B0000]/30 text-white"
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
              />
            </div>
            <DialogFooter className="gap-2">
              <DialogClose asChild>
                <Button variant="outline" type="button" className="border-[#8B0000]/30 text-gray-400">Cancel</Button>
              </DialogClose>
              <Button type="submit" className="bg-[#8B0000] hover:bg-[#B22222] text-white">
                Add Video
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MediaGrid({ items, loading, onDelete, onUpload, emptyLabel }) {
  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#8B0000] animate-spin" />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="text-center py-20 bg-white/[0.02] border border-dashed border-[#8B0000]/20 rounded-lg">
        <ImageIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-500 mb-4">{emptyLabel}</p>
        <Button onClick={onUpload} className="bg-[#8B0000] hover:bg-[#B22222] text-white">
          <Plus className="w-4 h-4 mr-2" /> Upload Image
        </Button>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {items.map(item => (
        <Card key={item.id} className="bg-[#1a1a1a]/80 border-[#8B0000]/30 group relative overflow-hidden">
          <div className="aspect-square relative overflow-hidden bg-black">
            {item.type === 'video' ? (
              <VideoThumbnail url={item.url} />
            ) : (
              <img
                src={getImageSrc(item)}
                alt={item.title}
                className="w-full h-full object-cover transition-transform group-hover:scale-110"
              />
            )}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Button
                size="icon"
                variant="destructive"
                onClick={() => onDelete(item.id)}
                className="rounded-full w-10 h-10"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <CardFooter className="p-3">
            <span className="text-gray-300 text-sm font-medium truncate">{getDisplayName(item)}</span>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}
